import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Img from "@/components/Img";
import { ABOUT_IMAGE, BRANDS } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

export default function AIgniva() {
  const { t, lp } = useLanguage();

  return (
    <div data-testid="about-page" className="pt-20">
      <Seo title={t("seo.about.title")} description={t("seo.about.desc")} />

      <section className="pt-16 lg:pt-20 pb-16 lg:pb-20 max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <Reveal className="lg:col-span-7">
          <span className="mono-tag text-xs uppercase tracking-[0.22em] text-[#C2A675]">{t("about.tag")}</span>
          <h1 className="serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4 mb-6 leading-[1.05]">{t("about.title")}</h1>
          <p className="text-base sm:text-lg text-[#A8A6A0] font-light leading-relaxed max-w-2xl">{t("about.lead")}</p>
          <p className="text-sm sm:text-base text-[#A8A6A0] font-light leading-relaxed max-w-2xl mt-5">{t("about.experience")}</p>
          <div className="mt-8 border-l-2 border-[#C2A675] pl-6 py-1 max-w-2xl">
            <p className="serif-display text-xl text-white font-light">{t("about.note")}</p>
            <p className="text-sm text-[#A8A6A0] font-light mt-2 leading-relaxed">{t("about.note_text")}</p>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="img-frame anim-wipe aspect-[4/5] border border-white/15">
            <Img id={ABOUT_IMAGE} alt={t("about.tag")} priority className="absolute inset-0 w-full h-full object-cover img-tone" />
          </div>
        </Reveal>
      </section>

      <section className="border-y border-white/10 bg-[#0E0F12]">
        <div className="py-16 lg:py-20 max-w-7xl mx-auto px-6">
          <Reveal className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <span className="mono-tag text-xs uppercase tracking-[0.22em] text-[#C2A675]">{t("about.pillars_tag")}</span>
              <h2 className="serif-display text-3xl sm:text-4xl lg:text-5xl font-light text-white mt-3">{t("about.pillars_title")}</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {t("about.pillars").map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08} className="p-7 lg:p-8 bg-[#121316] hover:bg-[#181A1F] transition-colors group flex flex-col">
                <div className="serif-display text-4xl text-[#C2A675]/70 group-hover:text-[#C2A675] transition-colors mb-8">{p.n}</div>
                <h3 className="serif-display text-2xl font-light text-white mb-3 leading-tight">{p.t}</h3>
                <p className="text-sm text-[#A8A6A0] leading-relaxed font-light">{p.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <Reveal className="lg:col-span-5">
            <span className="mono-tag text-xs uppercase tracking-[0.22em] text-[#C2A675]">{t("about.rel_tag")}</span>
            <h2 className="serif-display text-3xl sm:text-4xl lg:text-5xl font-light text-white mt-4 leading-tight">{t("about.rel_title")}</h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <p className="text-base sm:text-lg text-[#A8A6A0] font-light leading-relaxed mb-8">{t("about.rel_text")}</p>
            <ul className="flex flex-wrap gap-2 mb-10" data-testid="about-sectors">
              {t("home.sectors").map((s) => (
                <li key={s} className="px-3 py-1.5 border border-white/15 text-[11px] uppercase tracking-[0.14em] text-[#D6D3CC]">{s}</li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-4">
              <Link to={lp("/contactos")} data-testid="about-contact-cta" className="btn-solid px-8 py-4">
                {t("about.cta")} <ArrowRight size={14} aria-hidden="true" />
              </Link>
              <Link to={lp("/marcas")} data-testid="about-brands-cta" className="btn-ghost px-8 py-4">
                {t("nav.brands")}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {BRANDS.map((b, i) => (
            <Reveal key={b.slug} delay={i * 0.06}>
              <Link to={lp(`/marcas/${b.slug}`)} data-testid={`about-brand-${b.slug}`} className="group block">
                <p className="serif-display text-2xl sm:text-3xl text-white font-light group-hover:text-[#C2A675] transition-colors">{b.name}</p>
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#A8A6A0] mt-1">{t(`brand.${b.slug}.tag`)}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
